/**
 * `give <item> to <actor>` and `show <item> to <actor>` (D235, actors round
 * B2; `docs/design/actors.md` §4) — a thing passed to a body, answered from
 * the body's own table.
 *
 * @remarks
 * One handler for the pair, because they are one act read two ways: the
 * actor's `giveInteractions` is the `use` interaction's shape (an item, its
 * conditions, its lines, its effects) plus `consumesItem`, and the only
 * difference between handing a thing over and holding it up is whether the
 * body may KEEP it. `give` honors the arm's `consumesItem`; `show` never
 * does. So one authored answer serves both verbs — the occupant's "I cannot
 * see it through steel" answers `show the film to her` and `give the film
 * to her` alike, and the film stays in the hands either way.
 *
 * D244 (Act II §13.7): an arm may name the one verb it answers (`act`), so
 * a trade can happen on `give` and only an appraisal on `show`; an arm for
 * the thing that answers only the other verb is no arm for this one, and
 * the body refuses in its own words.
 *
 * The refusal ladder is `use`'s, rung for rung: the thing must be CARRIED
 * (`notCarried` — you offer what is in your hands); the body must have an
 * arm for the thing (else `refusesGiftsNarratives`, the actor's own words
 * for every gift it will not take, or the `giftRefused` slot); and the first
 * arm for the thing whose conditions hold speaks (else `conditionUnmet`,
 * as `use` says it). The lines stamp under `give:<itemId>>actorId` or
 * `show:<itemId>>actorId`, the `use` stamp's shape, so the Voices index
 * hears one act per verb.
 *
 * Reach is not this handler's question. A gift passes where the hands do
 * not — the intercept lets `give` and `show` through to a body that is
 * heard and not reached, exactly as it lets speech through — and the body
 * answers in its own words about the door between you. Step 2's catalog
 * check still refuses a body that is not even addressable.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';

import type { RuntimeAction } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireActor, requireItem } from '../bundle/bundle-lookups.js';
import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { speakSlot } from '../defaults/default-responses.js';
import { type ActionHandlerResult, rejected } from './action-handler-result.js';
import { stampNarrativeLines, type TranscriptCursor } from './transcript-cursor.js';
import { withEffects } from './with-effects.js';

/**
 * Pass a thing to a body — hand it over, or hold it up.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param action - The narrowed gift: the thing, the body, and which verb
 * @param cursor - This turn's transcript cursor
 * @returns The completed exchange, or the typed refusal
 */
export const handleGive = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'give' | 'show' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const thing = requireItem(bundle, action.itemId);
	const actor = requireActor(bundle, action.actorId);

	// Rung 1 — what is offered is in the hands.
	if (!state.world.inventoryItemIds.includes(action.itemId)) {
		return rejected(bundle, state, { itemId: action.itemId, type: 'not_carried' }, [speakSlot(bundle, state, 'notCarried', { itemName: thing.name })]);
	}

	// Rung 2 — the body has an arm for this thing at all, for THIS verb (D244:
	// an arm may answer `give` alone or `show` alone; absent, both).
	const arms = actor.giveInteractions.filter((interaction) => interaction.itemId === action.itemId && (interaction.act === undefined || interaction.act === action.type));

	if (arms.length === 0) {
		return rejected(
			bundle,
			state,
			{ actorId: action.actorId, itemId: action.itemId, type: 'gift_refused' },
			actor.refusesGiftsNarratives ?? [speakSlot(bundle, state, 'giftRefused', { itemName: thing.name })],
		);
	}

	// Rung 3 — the first arm whose conditions hold, in declared order.
	const arm = arms.find((candidate) => candidate.conditions.every((condition) => evaluateCondition(condition, bundle, state)));

	if (arm === undefined) {
		return rejected(bundle, state, { actorId: action.actorId, itemId: action.itemId, type: 'gift_condition_unmet' }, [speakSlot(bundle, state, 'conditionUnmet')]);
	}

	const spoken = stampNarrativeLines(bundle, state, cursor, arm.narratives, `${action.type}:${action.itemId}>${action.actorId}`);
	// The body keeps the thing only when it was GIVEN and the arm says so; a
	// thing shown is still in the hands. Keeping is `remove_item` (D230's
	// reading: out of the hands, off the face, in no room) folded FIRST — after
	// the lines, which may name the thing in the hand, and before the arm's
	// own effects, which may read that it is gone.
	const handedOver = action.type === 'give' && arm.consumesItem ? [{ itemId: action.itemId, type: 'remove_item' as const }] : [];

	return withEffects(bundle, { cursor: spoken.cursor, entries: spoken.entries, state, type: 'completed' }, [...handedOver, ...arm.effects]);
};
